// The Benchmark page (/sentinel-workspace/benchmark): golden cases to answer with form 1.6, for the account selected in
// the app. `?view=` picks To do (in progress first, then not started) or Done for Sentinels, Cases or Monitoring for
// editors; `?case=<id>` opens one case.

import { useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getApiErrorMessage, parseUtcDate } from '@/lib/utils';
import type { GoldenCaseRow, GoldenTab } from '@/types/golden.types';
import { ViewPicker } from '../view-picker';
import { BenchmarkCase } from './benchmark-case';
import { BenchmarkInfo } from './benchmark-info';
import { BenchmarkProgress } from './benchmark-progress';
import { benchmarkDoneQueryAtom, benchmarkInProgressQueryAtom, benchmarkTodoQueryAtom, goldenMeQueryAtom } from './benchmark-state';

export const CASE_PARAM = 'case';
export const VIEW_PARAM = 'view';
export type BenchmarkView = 'todo' | 'done' | 'cases' | 'monitoring';
type ListView = Exclude<BenchmarkView, 'monitoring'>;

const VIEW_LABELS: Record<BenchmarkView, string> = { todo: 'To do', done: 'Done', cases: 'Cases', monitoring: 'Monitoring' };
const SENTINEL_VIEWS: readonly BenchmarkView[] = ['todo', 'done'];
const EDITOR_VIEWS: readonly BenchmarkView[] = ['cases', 'monitoring'];
const STATUS_LABELS: Record<GoldenTab, string> = { todo: 'Not started', in_progress: 'In progress', done: 'Done' };
const COLUMNS: Record<ListView, string[]> = {
  todo: ['Case', 'Frame day', 'Channel', 'Received', 'Status'],
  done: ['Case', 'Frame day', 'Channel', 'Submitted', 'Minutes'],
  cases: ['Case', 'Frame day', 'Channel', 'Received'],
};
const NONE = '—';
/** "2026-10-29" reads "Oct 29"; anything else shows as it came. */
const frameDayLabel = (day: string) => {
  const date = parseISO(day);
  return isValid(date) ? format(date, 'MMM d') : day;
};
/** "email" reads "Email". */
const channelLabel = (channel: string) => channel.charAt(0).toUpperCase() + channel.slice(1);
export const EMPTY_TODO = 'No cases waiting. New cases arrive every morning (UTC).';
export const EMPTY_DONE = 'No submitted cases yet.';
export const EMPTY_ACCOUNT = 'No Benchmark cases for this account.';

function CaseTable({ rows, view, onOpen }: { rows: GoldenCaseRow[]; view: ListView; onOpen: (id: string) => void }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {COLUMNS[view].map((column) => (
            <TableHead key={column}>{column}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              {/* Sentinels open not-started cases only through Start next, in their fixed order. */}
              {view === 'todo' && row.status === 'todo' ? (
                row.shortId
              ) : (
                <Button variant="link" className="h-auto p-0 text-xs" onClick={() => onOpen(row.id)}>
                  {row.shortId}
                </Button>
              )}
            </TableCell>
            <TableCell className="tabular-nums">{frameDayLabel(row.frameDay)}</TableCell>
            <TableCell>{channelLabel(row.channel)}</TableCell>
            {view === 'done' ? (
              <>
                <TableCell className="tabular-nums">{row.submittedAt ? format(parseUtcDate(row.submittedAt), 'MMM d, HH:mm') : NONE}</TableCell>
                <TableCell className="tabular-nums">{row.secondsTotal === null ? NONE : (row.secondsTotal / 60).toFixed(1)}</TableCell>
              </>
            ) : (
              <TableCell className="tabular-nums">{row.received}</TableCell>
            )}
            {view === 'todo' && (
              <TableCell>
                {row.status === 'in_progress' ? (
                  <Button variant="link" className="h-auto p-0 text-xs" aria-label={`Continue ${row.shortId}`} onClick={() => onOpen(row.id)}>
                    Continue
                    <ArrowRight aria-hidden />
                  </Button>
                ) : (
                  STATUS_LABELS[row.status]
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function BenchmarkList({ view, onOpen, header }: { view: ListView; onOpen: (id: string) => void; header: React.ReactNode }) {
  // To do = in progress first, then not started; Done = submitted; Cases (editors) = every case, all To do to them.
  const list = useAtomValue(view === 'done' ? benchmarkDoneQueryAtom : view === 'todo' ? benchmarkInProgressQueryAtom : benchmarkTodoQueryAtom);
  const todo = useAtomValue(benchmarkTodoQueryAtom);
  const queries = view === 'todo' ? [list, todo] : [list];
  const failed = queries.find((query) => query.isError);
  const rows = view === 'todo' ? [...(list.data?.rows ?? []), ...(todo.data?.rows ?? [])] : (list.data?.rows ?? []);
  const counts = list.data?.counts;
  const accountEmpty = counts !== undefined && counts.todo + counts.in_progress + counts.done === 0;
  const next = todo.data?.rows[0];

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex flex-wrap items-center gap-3">
        {header}
        {view === 'todo' && (
          <div className="ml-auto">
            <Button size="sm" disabled={!next} onClick={() => next && onOpen(next.id)}>
              Start next
              <ArrowRight aria-hidden />
            </Button>
          </div>
        )}
      </div>
      {view === 'todo' && (
        <BenchmarkInfo title="What is Benchmark?">
          Answer each case with the review form. Your answers become the golden answers we use to measure the AI. Start next
          opens your next case, in a fixed order.
        </BenchmarkInfo>
      )}
      {view === 'cases' && (
        <BenchmarkInfo title="Read-only for admins">
          Open any case of this account to see every Sentinel&apos;s answer. Admins never submit answers.
        </BenchmarkInfo>
      )}
      {failed ? (
        <p role="alert" className="text-[13px] text-red-600">
          {getApiErrorMessage(failed.error, 'Could not load the cases.')}
        </p>
      ) : queries.some((query) => query.isPending) ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-[13px] text-gray-800">{accountEmpty ? EMPTY_ACCOUNT : view === 'done' ? EMPTY_DONE : EMPTY_TODO}</p>
      ) : (
        <CaseTable rows={rows} view={view} onOpen={onOpen} />
      )}
    </div>
  );
}

export function BenchmarkPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useAtomValue(goldenMeQueryAtom);
  const counts = useAtomValue(benchmarkTodoQueryAtom).data?.counts;
  const caseId = searchParams.get(CASE_PARAM);
  const editParams = (edit: (params: URLSearchParams) => void) =>
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      edit(params);
      return params;
    });
  const openCase = (id: string | null) => editParams((params) => (id ? params.set(CASE_PARAM, id) : params.delete(CASE_PARAM)));
  const pickView = (next: BenchmarkView) =>
    editParams((params) => {
      params.set(VIEW_PARAM, next);
      params.delete(CASE_PARAM);
    });

  if (me.isPending) return <Skeleton className="m-4 h-40 w-full rounded-lg" />;
  if (me.isError) {
    return (
      <div className="flex w-full flex-col items-start gap-3 p-4">
        <p role="alert" className="text-[13px] text-red-600">
          {getApiErrorMessage(me.error, 'Could not check your access to Benchmark.')}
        </p>
        <Button variant="outline" size="sm" onClick={() => void me.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (me.data.role === 'none') {
    return <p className="p-4 text-[13px] text-gray-800">Benchmark is only for the Sentinels of its brands.</p>;
  }

  const editor = me.data.role === 'editor';
  const asked = searchParams.get(VIEW_PARAM);
  // Each role has its own views; any other value (an old link, the other role's view) is the role's first.
  const view: BenchmarkView = editor ? (asked === 'monitoring' ? 'monitoring' : 'cases') : asked === 'done' ? 'done' : 'todo';
  const header = (
    <ViewPicker
      current={view}
      options={editor ? EDITOR_VIEWS : SENTINEL_VIEWS}
      labels={VIEW_LABELS}
      countUnit="Cases"
      counts={!counts ? {} : editor ? { cases: counts.todo } : { todo: counts.todo + counts.in_progress, done: counts.done }}
      onChange={pickView}
    />
  );

  if (view === 'monitoring') {
    return (
      <div className="flex h-full w-full min-w-0 flex-col gap-4 overflow-y-auto p-4">
        <div className="flex flex-col items-start gap-[4px]">
          {header}
          <p className="text-[10px] leading-[1.1] text-gray-800">Last 7 frame days</p>
        </div>
        <BenchmarkProgress />
      </div>
    );
  }
  // The back link names and opens the role's first list: "← To do" for Sentinels (also from Done), "← Cases" for editors.
  const home = editor ? 'cases' : 'todo';
  if (caseId) {
    return <BenchmarkCase key={caseId} caseId={caseId} me={me.data} onOpen={openCase} backLabel={VIEW_LABELS[home]} onBack={() => pickView(home)} />;
  }
  return <BenchmarkList view={view} onOpen={openCase} header={header} />;
}
